import { useEffect, useRef, type ReactNode } from 'react';

export function ErrorBox({ children }: { children: ReactNode }) {
  return (
    <div className="error-box" role="alert">
      {children}
    </div>
  );
}

/** A small modal confirm, in the app's own style; Escape and the backdrop cancel. */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const cancel = useRef(onCancel);
  cancel.current = onCancel;
  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusables = (): HTMLElement[] => Array.from(ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]') ?? []);
    const keepFocus = (e: FocusEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) focusables()[0]?.focus();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel.current(); }
      if (e.key === 'Tab') {
        const items = focusables();
        const next = e.shiftKey ? items.at(-1) : items[0];
        const edge = e.shiftKey ? items[0] : items.at(-1);
        if (document.activeElement === edge) { e.preventDefault(); next?.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', keepFocus);
    ref.current?.querySelector<HTMLElement>('button[data-confirm]')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', keepFocus);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);
  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="alertdialog" aria-modal="true" aria-label={title} ref={ref}>
        <h2>{title}</h2>
        <div className="modal-body">{body}</div>
        <div className="row modal-actions">
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" data-confirm className="btn btn-primary" disabled={busy} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="spinner" aria-live="polite">
      <span className="dot" />
      <span className="dot" />
      <span className="dot" />
      {label ? <span className="muted small"> {label}</span> : null}
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'ok' | 'warn' | 'bad' }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}
